import { forwardRef } from 'react';
import Dialog, { type DialogProps } from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Slide from '@mui/material/Slide';
import { type TransitionProps } from '@mui/material/transitions';
import CloseIcon from '@mui/icons-material/Close';

const SlideTransition = forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export interface AppDialogProps extends Omit<DialogProps, 'title'> {
  /**
   * Dialog title
   */
  title: React.ReactNode;
  /**
   * Optional subtitle under the title
   */
  subtitle?: React.ReactNode;
  /**
   * Action buttons for the footer
   */
  actions?: React.ReactNode;
  /**
   * Show close button in header
   */
  showCloseButton?: boolean;
  /**
   * Callback when close button is clicked
   */
  onClose?: () => void;
  /**
   * Whether to use slide transition
   */
  slideUp?: boolean;
  /**
   * Disable closing on backdrop click
   */
  disableBackdropClose?: boolean;
}

/**
 * AppDialog - Modal dialog component
 *
 * Wraps MUI Dialog with our design system patterns.
 * Use for confirmations, forms, and focused user interactions.
 */
export const AppDialog = forwardRef<HTMLDivElement, AppDialogProps>(
  (
    {
      title,
      subtitle,
      actions,
      showCloseButton = true,
      onClose,
      slideUp = true,
      disableBackdropClose = false,
      children,
      ...props
    },
    ref
  ) => {
    const handleClose = (event: object, reason: 'backdropClick' | 'escapeKeyDown') => {
      if (disableBackdropClose && reason === 'backdropClick') {
        return;
      }
      onClose?.();
    };

    return (
      <Dialog
        ref={ref}
        onClose={handleClose}
        TransitionComponent={slideUp ? SlideTransition : undefined}
        {...props}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
            pb: subtitle ? 0.5 : 2,
          }}
        >
          <Box>
            <Typography variant="h6" component="span">
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          {showCloseButton && onClose && (
            <IconButton
              aria-label="close"
              onClick={onClose}
              size="small"
              sx={{
                color: 'text.secondary',
                mt: -0.5,
                mr: -0.5,
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent dividers={!!actions}>{children}</DialogContent>
        {actions && (
          <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>{actions}</DialogActions>
        )}
      </Dialog>
    );
  }
);

AppDialog.displayName = 'AppDialog';

// Re-export for flexibility
export { DialogTitle, DialogContent, DialogActions };
